import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { collectFormErrors } from '@shared/formHelpers';
import { ExpectedActivitySurvey } from '@shared/models/data-model';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { GoogleTagManagerService } from '@shared/services/google-tag-manager.service';
import { RegistrationService } from '@shared/services/registration.service';
import { REGISTRATION_STEP_ID } from '@shared/services/steps.service';
import { NGXLogger } from 'ngx-logger';

@Component({
    selector: 'number-of-cards',
    templateUrl: './number-of-cards.component.html',
    styles: ``
})
export class NumberOfCardsComponent implements OnInit {
    private readonly subStepName = 'Expected Number of Cards';
    private expectedActivitySurveyToSave: ExpectedActivitySurvey;
    numberOfCardsOptions = [
        { label: '1-9', value: 5 },
        { label: '10-49', value: 30 },
        { label: '50-99', value: 75 },
        { label: '100-249', value: 175 },
        { label: '250+', value: 251 }
    ];
    form: UntypedFormGroup;
    loading = false;

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly registrationService: RegistrationService,
        private readonly amplitudeService: AmplitudeService,
        private readonly gtmService: GoogleTagManagerService,
        private readonly logger: NGXLogger,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dynatrace: DynatraceHelperService
    ) { }

    ngOnInit(): void {
        this.trackSubStepViewed();
        const expectedActivitySurvey = this.route.snapshot.data.expectedActivitySurvey;
        this.expectedActivitySurveyToSave = {
            ...expectedActivitySurvey
        };
        this.form = this.fb.group({
            numberOfCards: [expectedActivitySurvey.numberOfCards, Validators.required]
        });
    }

    private trackSubStepViewed(): void {
        this.amplitudeService.trackSubStepViewed(this.subStepName);
        this.gtmService.trackSubStepViewed(this.subStepName);
    }

    private trackSubStepCompleted(): void {
        this.amplitudeService.trackSubStepCompleted(this.subStepName);
        this.gtmService.trackSubStepCompleted(this.subStepName);
    }

    previousStep(): void {
        const backActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.BACK_TO_PREVIOUS);
        this.router.navigate([this.registrationService.getPreviousStep()]).then(() => {
            this.dynatrace.leaveAction(backActionId);
        });
    }

    async nextStep() {
        if (this.form.valid) {
            this.loading = true;

            const submitActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.SUBMIT_FORM);

            this.expectedActivitySurveyToSave.numberOfCards = this.form.get('numberOfCards').value;
            try {
                await this.registrationService.saveExpectedActivitySurvey(this.expectedActivitySurveyToSave);
                this.trackSubStepCompleted();
                this.registrationService.completeStep(REGISTRATION_STEP_ID.NUMBER_OF_CARDS);
                this.router.navigate([this.registrationService.getNextStep()]).then(() => {
                    this.dynatrace.leaveAction(submitActionId);
                });
            } catch (e) {
                this.logger.error('Save expected activity survey failed', e, 'with expected activity survey', this.expectedActivitySurveyToSave);
                this.loading = false;
                this.dynatrace.leaveAction(submitActionId);
            }
        } else {
            this.form.markAllAsTouched();
            this.amplitudeService.trackFormSubmissionRejected(this.route.snapshot.data['pageName'], collectFormErrors(this.form));
        }
    }
}
